import {
  useWSSnackbar,
  WSActions,
  WSCheckboxToggle,
  WSDivider,
  WSForm,
  WSInfoBox,
  WSInputText,
  WSList,
  WSSelect
} from "@wingspanhq/fe-component-library";
import {
  CustomFieldResourceType,
  EngagementStatus,
  ICustomField,
  IPayeeResponse,
  WSDataTypes
} from "@wingspanhq/payments/dist/interfaces";
import orderBy from "lodash/orderBy";
import React from "react";
import * as Yup from "yup";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useQueryCustomFieldsAll } from "../../../../query/customFields/queries/useQueryCustomFieldsAll";
import { useQueryEngagementsListAll } from "../../../../query/engagements/queries/useQueryEngagementsListAll";
import { useUserId } from "../../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import {
  useQueryIntegrationsQuickbooks,
  useQueryIntegrationsQuickbooksAccountExpenses,
  useQueryIntegrationsQuickbooksVendors
} from "../../../../query/integrations/queries";
import { useMutationCreatePayee } from "../../../../query/payee/mutations/useMutationCreatePayee";
import { useQueryUserProfile } from "../../../../query/users/queries";
import { WSQueries } from "../../../../query/WSQuery";
import { selectorIsEnterpriseUser } from "../../../../shared/selectors/selectorIsEnterpriseUser";
import { validatorEmail } from "../../../../shared/validators/validatorEmail";
import { ErrorContextKey } from "../../../../services/platform";
import {
  getDefaultValuesForAdditionalData,
  getYupValidationSchema
} from "../../../../utils/jsonFormSchema";
import {
  contactCompanyValidator,
  contactNameValidator
} from "../../../../utils/validators";
import { selectorEngagementOptions } from "../../../EngagementTypes/selectors/selectorEngagementOptions";
import {
  FormPartialPayeeQboMapping,
  PAYEE_QBO_MAPPING_FORM_DEFAULT_VALUES,
  PayeeQboMappingFormValues
} from "../../../Integrations/components/FormPartialPayeeQboMapping";
import { selectorIsPayeeQboMappingEnabled } from "../../../Integrations/selectors/selectorIsPayeeQboMappingEnabled";
import { useCreateContractorBulkAction } from "../../hooks/useCreateContractorBulkAction";
import { ICustomFields } from "@wingspanhq/payments/dist/interfaces/customFields";

export interface CreateContractorModalProps {
  onClose(result?: IPayeeResponse): void;

  onSuccess?: (client?: IPayeeResponse) => Promise<void>;
}

type FormData = Partial<PayeeQboMappingFormValues> & {
  company: string;
  customFields?: ICustomFields;
  email: string;
  engagementIds?: string[];
  externalId?: string;
  name: string;
};

export const CreateContractorModal: React.FC<CreateContractorModalProps> = ({
  onClose,
  onSuccess
}) => {
  const { openSnackbar } = useWSSnackbar();

  const userId = useUserId();

  const queryUserProfile = useQueryUserProfile(userId);

  const queryEngagements = useQueryEngagementsListAll({
    filter: {
      isDefault: false,
      status: EngagementStatus.Active
    }
  });

  const [createPayee, createPayeeMeta] = useMutationCreatePayee();
  const runCreateContractorBulkAction = useCreateContractorBulkAction();

  const queryFeatureFlags = useFeatureFlags();
  const queryIntegrationsQuickbooks = useQueryIntegrationsQuickbooks();
  const queryIntegrationsQuickbooksVendors =
    useQueryIntegrationsQuickbooksVendors();
  const queryIntegrationsQuickbooksExpenseAccounts =
    useQueryIntegrationsQuickbooksAccountExpenses();

  const isQboMappingEnabled = selectorIsPayeeQboMappingEnabled({
    expenseAccounts: queryIntegrationsQuickbooksExpenseAccounts.data,
    featureFlags: queryFeatureFlags?.data,
    qboService: queryIntegrationsQuickbooks.data,
    vendors: queryIntegrationsQuickbooksVendors.data
  });

  const queryCollaboratorCustomFields = useQueryCustomFieldsAll({
    resourceType: [CustomFieldResourceType.Collaborator]
  });

  const prepareDynamicFormFields = (
    customFields: ICustomField[],
    prefix?: string
  ) => {
    return orderBy(customFields, "type", "desc").map(field => {
      switch (field.type) {
        case WSDataTypes.String:
          return (
            <WSForm.Field
              key={field.key}
              name={prefix ? `${prefix}.${field.key}` : field.key}
              label={`${field.name}${field.required ? " *" : ""}`}
              component={WSInputText}
              componentProps={{
                placeholder: field.name
              }}
            />
          );
        case WSDataTypes.Boolean:
          return (
            <WSForm.Field
              key={field.key}
              name={prefix ? `${prefix}.${field.key}` : field.key}
              component={WSCheckboxToggle}
              componentProps={{ label: field.name }}
            />
          );
        default:
          return (
            <WSForm.Field
              key={field.key}
              name={prefix ? `${prefix}.${field.key}` : field.key}
              label={`${field.name}${field.required ? " *" : ""}`}
              component={WSInputText}
              componentProps={{
                placeholder: field.name
              }}
            />
          );
      }
    });
  };

  return (
    <WSQueries
      queries={{
        queryUserProfile,
        queryCollaboratorCustomFields,
        queryEngagements
      }}
    >
      {({
        queryUserProfileData: userProfile,
        queryCollaboratorCustomFieldsData: collaboratorCustomFields,
        queryEngagementsData: engagements
      }) => {
        const isEnterpriseUser = selectorIsEnterpriseUser(userProfile);
        const customFieldsValidationSchema = getYupValidationSchema(
          collaboratorCustomFields
        );
        const engagementOptions = selectorEngagementOptions(engagements);
        return (
          <WSForm<FormData>
            defaultValues={{
              ...PAYEE_QBO_MAPPING_FORM_DEFAULT_VALUES,
              company: "",
              customFields: getDefaultValuesForAdditionalData(
                collaboratorCustomFields
              ),
              email: "",
              engagementIds: [] as string[],
              externalId: "",
              name: ""
            }}
            validationSchema={Yup.object().shape({
              company: contactCompanyValidator,
              customFields: Yup.object().shape(customFieldsValidationSchema),
              email: validatorEmail.required("Required"),
              name: contactNameValidator
            })}
            onSubmit={async data => {
              const selectedEngagementIds = data.engagementIds || [];
              const isSingleSelection = selectedEngagementIds.length <= 1;

              if (isSingleSelection) {
                const qboPayload =
                  isQboMappingEnabled &&
                  (data.qboVendorId || data.qboExpenseAccountId)
                    ? {
                        integration: {
                          quickbooks: {
                            expenseAccountId: data.qboExpenseAccountId,
                            vendorId: data.qboVendorId
                          }
                        }
                      }
                    : {};

                const result = await createPayee({
                  email: data.email,
                  engagementId: selectedEngagementIds[0],
                  payerOwnedData: {
                    ...qboPayload,
                    customFields: data.customFields,
                    payeeCompanyName: data.company,
                    payeeExternalId: data.externalId,
                    payeeName: data.name
                  }
                });

                if (!result) return;
                await onSuccess?.(result);
                onClose(result);
                openSnackbar({
                  message: "Contractor added successfully",
                  type: "success"
                });
              } else {
                const result = await runCreateContractorBulkAction({
                  payeeCreationData: {
                    email: data.email,
                    payerOwnedData: {
                      customFields: data.customFields,
                      payeeCompanyName: data.company,
                      payeeExternalId: data.externalId,
                      payeeName: data.name
                    }
                  },
                  engagements: selectedEngagementIds.map(id => ({
                    id,
                    name:
                      engagementOptions.find(o => o.value === id)?.label || id
                  }))
                });

                if (!result) return;
                await onSuccess?.(result);
                onClose(result);
              }
            }}
          >
            <WSList gap="L">
              <WSInfoBox>
                After adding a contractor, they will be invited by the email
                provided to sign-up for a Wingspan account in order to receive
                payments from you.
              </WSInfoBox>

              <WSForm.Field
                name="email"
                label="Contractor email *"
                component={WSInputText}
                componentProps={{
                  helperText: "Contractor’s personal or business email",
                  tooltip: {
                    dark: true,
                    title: "Contractor email (required)",
                    children: `Send an invite to the contractor’s personal email or the email of the contractor’s business that they operate under.

We do not recommend using emails provided by your company since the contractor’s account is portable for their own use as a contractor.`
                  },
                  placeholder: "Contractor email"
                }}
              />
              <WSForm.Field
                name="company"
                label="Contractor company"
                component={WSInputText}
                componentProps={{
                  tooltip: {
                    dark: true,
                    title: "Contractor company (optional)",
                    children: `If the contractor operates under their own contractor business name, enter it here. This information will be confirmed by the contractor when they onboard.`
                  },
                  placeholder: "Contractor company"
                }}
              />
              <WSForm.Field
                name="name"
                label="Contractor full name"
                component={WSInputText}
                componentProps={{
                  tooltip: {
                    dark: true,
                    title: "Contractor full name (optional)",
                    children: `This information will be confirmed by the contractor when they onboard.`
                  },
                  placeholder: "Contractor full name"
                }}
              />
              {prepareDynamicFormFields(
                collaboratorCustomFields,
                "customFields"
              )}
              {isEnterpriseUser && (
                <>
                  <WSDivider my="S" />
                  <WSForm.Field
                    name="externalId"
                    label="External ID"
                    component={WSInputText}
                    componentProps={{
                      placeholder: "External ID"
                    }}
                  />
                </>
              )}
              {engagementOptions.length > 0 && (
                <>
                  <WSDivider my="S" />
                  <WSForm.Field
                    name="engagementIds"
                    label="Engagements"
                    component={WSSelect}
                    componentProps={{
                      mode: "multiple",
                      placeholder: "Select engagements",
                      tooltip: {
                        dark: true,
                        title: "Engagements (optional)",
                        children: `An engagement is a type of work arrangement that allows payers to organize their diverse work relationships in a flexible way. You can assign one or more engagements when adding a new contractor.

If you have not set up engagements or do not plan to use engagements, you can skip this field. Engagements can be updated in the Contractors section of the app at any time.`
                      },
                      options: engagementOptions
                    }}
                  />
                </>
              )}

              <WSForm.Value name="engagementIds">
                {(engagementIds: string[] | null) => (
                  <FormPartialPayeeQboMapping
                    disabled={(engagementIds || [])?.length > 1}
                  />
                )}
              </WSForm.Value>

              <WSErrorMessage
                error={createPayeeMeta.error}
                contextKey={ErrorContextKey.CreatePayee}
              />

              <WSActions
                mt="XL"
                alignment="fill"
                buttons={[
                  {
                    label: "Add contractor",
                    kind: "Primary",
                    type: "submit",
                    loading: createPayeeMeta.isLoading
                  },
                  {
                    label: "Cancel",
                    kind: "Secondary",
                    type: "button",
                    onClick() {
                      onClose();
                    }
                  }
                ]}
              />
            </WSList>
          </WSForm>
        );
      }}
    </WSQueries>
  );
};
